import {
  ConfirmationDialog,
  date,
  DetailColumns,
  DetailPane,
  FormDialog,
  useDialogs,
  useErrors
} from '@management-ui/core';
import {CopyAll, Delete, Edit, RestoreFromTrash} from '@mui/icons-material';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import ConfirmDate from '../../../../components/ConfirmDate';
import OrderAdmin from '../../../../components/OrderAdmin';
import {ServiceContext} from '../../../../components/Services';
import routes from '../../../../routes';
import SalesOrderForm from '../../forms/SalesOrderForm';
import Tabs from './Tabs';
import Wrapper from './Wrapper';
import Value from '../../../../components/Value';

const SalesOrder = ({archive = false}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({salesOrder: 'Sorry the order could not be accessed'}), [])
  );

  const {openDialogs, toggleDialog} = useDialogs(['core', 'lock', 'unlock', 'delete', 'restore']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const loadOrder = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.salesOrder.getOrder(id, archive).then(retrieved => {
        setOrder(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('salesOrder', true);
        setLoading(false);
        setTimeout(() => {
          history.push(archive ? reverse(routes.archive.index, {tab: 'sales-orders'}) : routes.salesOrders.index);
        }, 3000);
      });
    }
  }, [archive, services, history, toggleError]);

  useEffect(() => {
    if (match.params?.id) {
      loadOrder(match.params?.id);
    }
  }, [history, match, order, loadOrder]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...(archive ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs)]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (order) {
      header = order.reference;
      trail = [
        ...(order.archived ? Wrapper.archiveCrumbs : Wrapper.standardCrumbs),
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [order]);

  const handleSaved = useCallback((updated) => {
    toggleDialog('core', false);
    if (updated) {
      setOrder(updated);
    } else if (order?.id) {
      loadOrder(order.id);
    }
  }, [toggleDialog, setOrder, loadOrder, order]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      setLoading(true);
      services.salesOrder.deleteOrder(order).then(() => {
        setLoading(false);
        history.push(routes.salesOrders.index);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);

  const handleRestore = useCallback((confirmed) => {
    toggleDialog('restore', false);
    if (confirmed) {
      setLoading(true);
      services.salesOrder.restoreOrder(order).then((restored) => {
        setLoading(false);
        history.push(reverse(routes.salesOrders.detail, {id: order.id}));
        setOrder(restored);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);

  const handleDuplicate = useCallback((confirmed) => {
    toggleDialog('duplicate', false);
    if (confirmed) {
      setLoading(true);
      services.salesOrder.duplicateOrder(order).then((duplicate) => {
        setLoading(false);
        history.push(reverse(routes.salesOrders.detail, {id: duplicate.id}));
        setOrder(duplicate);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, order]);


  return (
    <Wrapper title={title} order={order} loading={loading} crumbs={crumbs} errors={errors}>
      {order?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Core Details"
              actions={order.archived ? [
                {title: 'Restore', icon: <RestoreFromTrash/>, onClick: () => toggleDialog('restore', true)},
              ] : [
                ...(order.locked ? [] : [
                  {title: 'Edit', icon: <Edit/>, onClick: () => toggleDialog('core', true)},
                  {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
                ]),
                {title: 'Duplicate', icon: <CopyAll/>, onClick: () => toggleDialog('duplicate', true)}
              ]}
              details={[
                {
                  title: 'Customer',
                  value: order.customer?.name ?? '-',
                  route: order.customer ? reverse(routes.contacts.detail, {id: order.customer.id}) : null
                },
                {
                  title: 'Loading Date',
                  value: (
                    <ConfirmDate
                      order={order}
                      field="loading_date"
                      service={services.salesOrder.saveOrder}
                      onUpdated={setOrder}
                    />
                  )
                },
                {title: 'Date', value: order.date ? date(order.date) : '-'},
                {title: 'Order Number', value: order.reference ?? '-'},
                {title: 'Currency', value: order.currency},
                {title: 'No. of Loads', value: order.number_of_loads ?? '-'},
                {
                  title: 'Purchase Order',
                  value: order.purchase_order?.reference ?? '-',
                  route: order.purchase_order ? reverse(routes.purchaseOrders.detail, {id: order.purchase_order.id}) : null
                },
                {
                  title: 'Margin',
                  value: order.purchase_order ? <Value value={order.margin} currency={order.currency}/> : '-'
                },
              ]}
              dialogs={[
                (props) => (
                  <FormDialog
                    {...props}
                    title="Edit Sales Order"
                    open={openDialogs['core'] ?? false}
                    onClose={() => toggleDialog('core', false)}
                    render={(props) => (
                      <SalesOrderForm
                        {...props}
                        order={order}
                        fields="core"
                        onSaved={handleSaved}/>
                    )}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['delete'] ?? false}
                    title="Delete Sales Order"
                    message="Are you sure you want to delete this order?"
                    onClose={handleDelete}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['restore'] ?? false}
                    title="Restore Sales Order"
                    message="Are you sure you want to restore this order?"
                    onClose={handleRestore}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['duplicate'] ?? false}
                    title="Duplicate Sales Order"
                    message="Are you sure you want to duplicate this order?"
                    onClose={handleDuplicate}
                  />
                )
              ]}
            />,

            <OrderAdmin
              type="Sales"
              order={order}
              onOrderUpdated={setOrder}
              service={services.salesOrder}
              onLoading={setLoading}
            />,
          ]}/>

          <Tabs order={order} onOrderUpdated={setOrder}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default SalesOrder;
